import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

/**
 * The sidebar from the design system.
 *
 *   --ink-900 ground, --sidebar-w wide, collapsing to --sidebar-w-min
 *   rows are quiet white at 62%, lifting to 92% under the pointer
 *   the active row takes a --primary-bright wash and a --gold left rule
 *   a nested group indents its rows so their labels line up with the parent's
 *
 * The white alphas are the design file's own values. They are overlays on the
 * ink ground rather than palette colours, and the palette has no token for
 * them — see FINDINGS.
 */

/** Shared row geometry: brand, group headers and leaves are all this shape. */
const row = css`
  display: flex;
  align-items: center;
  gap: var(--s3);
  width: 100%;
  padding: var(--s2) var(--s3);
  border: none;
  border-radius: var(--r-sm);
  background: none;
  font-family: var(--font-ui);
  font-size: var(--fs-body);
  font-weight: var(--fw-regular);
  line-height: var(--lh-body);
  color: rgba(255, 255, 255, 0.62);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  position: relative;
  transition:
    background-color 0.12s,
    color 0.12s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.92);
  }

  /*
   * The menu builder paints every icon with a presentation attribute
   * (fill="#979797"). CSS outranks it, so the icon follows the row's colour
   * through all four states without the builder knowing anything about them.
   */
  svg.injected-svg {
    width: 17px;
    height: 17px;
    fill: currentColor;
  }

  .stroke svg.injected-svg {
    fill: none;
    stroke: currentColor;
  }
`;

/** The active row, on both levels. */
const activeRule = css`
  content: '';
  position: absolute;
  left: 0;
  top: var(--s1);
  bottom: var(--s1);
  width: 2px;
  border-radius: 0 2px 2px 0;
`;

export const Icon = styled.span`
  display: grid;
  place-items: center;
  flex: 0 0 17px;
  line-height: 0;
  color: inherit;
`;

export const Label = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Chevron = styled.span`
  display: grid;
  place-items: center;
  flex: 0 0 13px;
  line-height: 0;
  opacity: 0.5;
  transition: transform 0.16s;

  svg {
    width: 13px;
    height: 13px;
  }
`;

export const NavLink = styled(Link)`
  ${row}

  &.active {
    background: rgba(34, 184, 204, 0.12);
    color: var(--text-invert);
    font-weight: var(--fw-medium);
  }

  &.active::before {
    ${activeRule}
    background: var(--gold);
  }
`;

export const GroupButton = styled.button`
  ${row}

  &.active {
    color: rgba(255, 255, 255, 0.92);
  }

  &.open ${Chevron} {
    transform: rotate(180deg);
  }
`;

export const Sub = styled.div`
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.2s;

  &.open {
    /* Tall enough for the longest group (Cafeteria, seven rows) at --fs-body. */
    max-height: 420px;
  }

  /* The indent lines a child's label up with its parent's: row padding, plus
     the icon it does not have, plus the gap after it.

     A nested row reads at the same size as its parent — the level is carried
     by the indent, the rule and the quieter ink, not by shrinking the text.
     The design file steps this down to 12.5px; at that size the rail was the
     hardest thing in the shell to read, so every nav label is --fs-body. */
  ${NavLink} {
    padding-left: calc(var(--s4) + var(--s5));
    font-size: var(--fs-body);
    color: rgba(255, 255, 255, 0.5);
  }

  ${NavLink}.active {
    background: transparent;
    color: var(--primary-bright);
  }

  ${NavLink}.active::before {
    left: var(--s5);
    background: var(--primary-bright);
  }
`;

export const Group = styled.div``;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s3);
  height: var(--h-topbar);
  flex: 0 0 var(--h-topbar);
  padding: 0 var(--s4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
`;

export const BrandMark = styled.img`
  width: var(--h-avatar);
  height: var(--h-avatar);
  flex: 0 0 var(--h-avatar);
  border-radius: var(--r-sm);
`;

/* The one string in the shell that is neither body nor label: --fs-title, the
   same size the topbar draws the page title at. See FINDINGS for the ruling. */
export const BrandName = styled.span`
  font-size: var(--fs-title);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-body);
  color: var(--text-invert);
  white-space: nowrap;
`;

export const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--s3) var(--s2) var(--s5);

  &::-webkit-scrollbar {
    width: var(--s2);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.08);
    border-radius: var(--r-pill);
  }
`;

export const SideContainer = styled.aside`
  display: flex;
  flex-direction: column;
  width: var(--sidebar-w);
  flex: 0 0 var(--sidebar-w);
  height: 100%;
  background: var(--ink-900);
  overflow: hidden;
  transition:
    width 0.18s,
    flex-basis 0.18s;

  /*
   * Collapsed: icons only. Labels and chevrons leave the layout rather than
   * fading, so nothing is left half-drawn at --sidebar-w-min, and open groups
   * close — a nested list has nothing to indent against at 64px wide.
   */
  &.collapsed {
    width: var(--sidebar-w-min);
    flex-basis: var(--sidebar-w-min);

    ${Brand},
    ${NavLink},
    ${GroupButton} {
      justify-content: center;
      padding-left: var(--s2);
      padding-right: var(--s2);
    }

    ${BrandName},
    ${Label},
    ${Chevron} {
      display: none;
    }

    ${Sub} {
      max-height: 0;
    }

    /*
     * Collapsed, the group icon has to say where you are.
     *
     * A group heading is deliberately quiet in the open rail — the active
     * child below it carries the cyan text and the cyan rule, and marking
     * both would say the same thing twice. At 64px that child is inside a
     * ${Sub} pinned to zero height, so nothing on the rail is marked at all
     * and eleven identical icons are the whole of the navigation. Handing the
     * group the leaf's treatment for the width of the collapse fixes it
     * without touching the open rail.
     */
    ${GroupButton}.active {
      background: rgba(34, 184, 204, 0.12);
      color: var(--text-invert);
      font-weight: var(--fw-medium);
    }

    ${GroupButton}.active::before {
      ${activeRule}
      background: var(--gold);
    }
  }
`;
